import type {
  ClassroomSubjectItem,
  CreateTeachingPayload,
  DescriptionPayload,
  NewClassroomSubjectItemPayload,
  NewTeachingItem,
  Res,
  Section,
  StudyPlanPayload,
  TeachingAttendanceJustifyPayload,
  TeachingDetails,
  TeachingHourPayload,
  TeachingItem,
  TeachingList,
  TeachingSectionAnswerBatchUpdatePayload,
  TeachingSectionQuestion,
  UpdateClassroomSubjectItemPayload,
} from '@/types'
import { removeNullProps } from '@/utils'

import base from './base'

export const teachingApi = base.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getTeachings: builder.query<TeachingList[], void>({
      query: () => 'teachers/teachings',
      transformResponse: (res: Res<TeachingList[]>) => res.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Teaching' as const, id })),
              { type: 'Teaching' as const, id: 'LIST' },
            ]
          : [{ type: 'Teaching' as const, id: 'LIST' }],
    }),

    getSections: builder.query<Section[], void>({
      query: () => 'sections',
      transformResponse: (res: Res<Section[]>) => res.data,
    }),

    getTeachingDetails: builder.query<TeachingDetails, string>({
      query: (teachingId) => `teachers/teachings/${teachingId}`,
      transformResponse: (res: Res<TeachingDetails>) => res.data,
      providesTags: (_, __, id) => [{ type: 'Teaching' as const, id }],
    }),

    updateTeaching: builder.mutation<void, Partial<TeachingDetails>>({
      query: (data) => ({
        url: `teachers/teachings/${data.id}`,
        method: 'PUT',
        body: removeNullProps({ ...data }),
      }),
      onQueryStarted({ id = '', ...patch }, { dispatch, queryFulfilled }) {
        const stringId = typeof id === 'string' ? id : id?.toString()
        const patchResult = dispatch(
          teachingApi.util.updateQueryData('getTeachingDetails', stringId, (draft) => {
            Object.assign(draft, patch)
          }),
        )
        queryFulfilled.catch(patchResult.undo)
      },
      invalidatesTags: (_, error, args) =>
        !error ? [{ type: 'Teaching' as const, id: args.id }] : [],
    }),

    addTeachingItem: builder.mutation<void, NewTeachingItem>({
      query: (body) => ({
        url: 'teachers/teaching-items',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (!error ? ['Teaching'] : []),
    }),

    updateTeachingItem: builder.mutation<void, Partial<TeachingItem>>({
      query: (data) => ({
        url: `teachers/teaching-items/${data.id}`,
        method: 'PUT',
        body: removeNullProps({ ...data }),
      }),
      invalidatesTags: (_, error) => (!error ? ['Teaching'] : []),
    }),

    deleteTeachingItem: builder.mutation<void, number>({
      query: (id) => ({
        url: `teachers/teaching-items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (!error ? ['Teaching'] : []),
    }),

    updateTeachingSection: builder.mutation<
      void,
      { teaching_id: number | string; section_id: number | string }
    >({
      query: (body) => ({
        url: 'teaching_sections',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error, args) =>
        !error ? [{ type: 'Teaching' as const, id: args.teaching_id }] : [],
    }),

    createOrUpdateTeachingHour: builder.mutation<void, TeachingHourPayload>({
      query: ({ teaching_id, ...body }) => ({
        url: `teachers/teaching-hours/${teaching_id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error, { teaching_id }) =>
        !error ? [{ type: 'Teaching', id: teaching_id }] : [],
    }),

    removeTeachingHour: builder.mutation<void, string>({
      query: (id) => ({
        url: `teachers/teaching-hours/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, id) => (!error ? [{ type: 'Teaching', id }] : []),
    }),

    addAttendanceJustify: builder.mutation<void, TeachingAttendanceJustifyPayload>({
      query: ({ id, justification }) => ({
        url: `teachers/teachings/${id}/attendance-justify`,
        method: 'POST',
        body: { justification },
      }),
      invalidatesTags: (_, error, { id }) => (!error ? [{ type: 'Teaching', id }] : []),
    }),

    removeAttendanceJustify: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `teachers/teachings/${id}/attendance-justify`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, id) => (!error ? [{ type: 'Teaching', id }] : []),
    }),

    getSectionQuestions: builder.query<TeachingSectionQuestion[], number>({
      query: (id) => `teachers/teachings/${id}/questions`,
      transformResponse: (res: Res<TeachingSectionQuestion[]>) => res.data,
      providesTags: ['TeachingSectionQuestions'],
    }),

    createSectionQuestion: builder.mutation<{ message: string }, FormData>({
      query: (body) => ({
        url: 'teachers/teaching-section-questions',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) =>
        !error ? ['TeachingSectionQuestions', 'ClassroomQuestionProgress'] : [],
    }),

    updateSectionQuestion: builder.mutation<
      { message: string },
      { id: number; question: string }
    >({
      query: ({ id, ...body }) => ({
        url: `teachers/teaching-section-questions/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error) => (!error ? ['TeachingSectionQuestions'] : []),
    }),

    deleteSectionQuestion: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `teachers/teaching-section-questions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) =>
        !error ? ['ClassroomQuestionProgress', 'TeachingSectionQuestions'] : [],
    }),

    giveSectionQuestionFeedback: builder.mutation<
      { message: string },
      TeachingSectionAnswerBatchUpdatePayload
    >({
      query: (body) => ({
        url: 'teachers/teaching-section-questions/batch-updates',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error) => (!error ? ['TeachingSectionQuestions'] : []),
    }),

    // NOVO
    getClassroomSubjectItems: builder.query<ClassroomSubjectItem[], string | number>({
      query: (classroomSubjectId) =>
        `teachers/classroom-subjects/${classroomSubjectId}/items`,
      transformResponse: (res: Res<ClassroomSubjectItem[]>) => res.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ClassroomSubjectItem' as const, id })),
              { type: 'ClassroomSubjectItem' as const, id: 'LIST' },
            ]
          : [{ type: 'ClassroomSubjectItem' as const, id: 'LIST' }],
    }),
    createClassroomSubjectItem: builder.mutation<
      { message: string },
      NewClassroomSubjectItemPayload
    >({
      query: ({ classroomSubjectId, ...body }) => ({
        url: `teachers/classroom-subjects/${classroomSubjectId}/items`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) =>
        !error ? [{ type: 'ClassroomSubjectItem', id: 'LIST' }] : [],
    }),

    updateClassroomSubjectItem: builder.mutation<
      { message: string },
      UpdateClassroomSubjectItemPayload
    >({
      query: ({ id, ...body }) => ({
        url: `teachers/classroom-subject-items/${id}`,
        method: 'PUT',
        body: removeNullProps({ ...body }),
      }),
      invalidatesTags: (_, error, { id }) =>
        !error
          ? [
              { type: 'ClassroomSubjectItem', id },
              { type: 'ClassroomSubjectItem', id: 'LIST' },
            ]
          : [],
    }),

    deleteClassroomSubjectItem: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `teachers/classroom-subject-items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, id) =>
        !error
          ? [
              { type: 'ClassroomSubjectItem', id },
              { type: 'ClassroomSubjectItem', id: 'LIST' },
            ]
          : [],
    }),

    createOrUpdateDescription: builder.mutation<{ message: string }, DescriptionPayload>({
      query: ({ classroomSubjectId, description }) => {
        return {
          url: `teachers/classroom-subjects/${classroomSubjectId}/study-plans/description`,
          method: 'POST',
          body: { description },
        }
      },
      invalidatesTags: (_, error) => (!error ? ['Classroom'] : []),
    }),

    createStudyPlan: builder.mutation<{ message: string }, StudyPlanPayload>({
      query: ({ classroomSubjectId, file, file_name }) => {
        const formData = new FormData()

        if (file_name !== undefined && file_name !== null) {
          formData.append('file_name', file_name)
        }

        if (file) {
          formData.append('file', file)
        }

        return {
          url: `teachers/classroom-subjects/${classroomSubjectId}/study-plans/file`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: (_, error) => (!error ? ['Classroom'] : []),
    }),

    deleteStudyPlan: builder.mutation<{ message: string }, string | number>({
      query: (id) => ({
        url: `teachers/study-plans/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (!error ? ['Classroom'] : []),
    }),

    createTeaching: builder.mutation<{ message: string }, CreateTeachingPayload>({
      query: (body) => ({
        url: 'teachers/teachings',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (!error ? [{ type: 'Teaching', id: 'LIST' }] : []),
    }),

    deleteTeaching: builder.mutation<{ message: string }, number | string>({
      query: (id) => ({
        url: `teachers/teachings/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (!error ? [{ type: 'Teaching', id: 'LIST' }] : []),
    }),
  }),
})

export const {
  useGetTeachingsQuery,
  useGetTeachingDetailsQuery,
  useGetSectionsQuery,
  useUpdateTeachingMutation,
  useUpdateTeachingSectionMutation,
  useUpdateTeachingItemMutation,
  useDeleteTeachingItemMutation,
  useAddTeachingItemMutation,
  useCreateOrUpdateTeachingHourMutation,
  useRemoveTeachingHourMutation,
  useAddAttendanceJustifyMutation,
  useRemoveAttendanceJustifyMutation,
  useDeleteSectionQuestionMutation,
  useGetSectionQuestionsQuery,
  useCreateSectionQuestionMutation,
  useUpdateSectionQuestionMutation,
  useGiveSectionQuestionFeedbackMutation,

  // NOVOS
  useGetClassroomSubjectItemsQuery,
  useCreateClassroomSubjectItemMutation,
  useUpdateClassroomSubjectItemMutation,
  useDeleteClassroomSubjectItemMutation,
  useCreateOrUpdateDescriptionMutation,
  useCreateStudyPlanMutation,
  useDeleteStudyPlanMutation,
  useCreateTeachingMutation,
  useDeleteTeachingMutation,
} = teachingApi
